import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Wifi, WifiOff, Gavel, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAuction } from "@/hooks/useAuction";
import { TimerBar, SoldOverlay } from "@/pages/LiveDisplay";
import { Logo, StatusPill, PlayerAvatar, TeamBadge, Stat } from "@/components/opl/Common";
import { api, errMsg, fmtL, fmtTime } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BIDDABLE = ["LIVE", "GOING_ONCE", "GOING_TWICE"];

function useMyTeam() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/auction/me/my-team").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

export default function OwnerPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mine, reload] = useMyTeam();
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState(0);
  const onNotification = (n) => {
    if (n.team_id && n.team_id !== user.team_id) return;
    if (n.type === "outbid") toast.warning(n.message, { duration: 5000 });
    else if (["sold", "low_purse", "squad_full", "paused", "resumed", "announcement", "nominated"].includes(n.type)) toast(n.message);
  };
  const { snap, connected, offset, lastEvent } = useAuction({ club: mine?.team?.club_id, onNotification });
  useEffect(() => { if (lastEvent && ["sold", "reverse", "bid", "undo"].includes(lastEvent.event)) reload(); }, [lastEvent, reload]);
  useEffect(() => { setChoice(0); }, [snap?.state?.version]);

  const me = snap?.teams.find((t) => t.id === user.team_id);
  const st = snap?.state;
  const options = useMemo(() => {
    if (!snap?.min_next_bid) return [];
    const inc = snap.next_increment || (snap.config.bid_increments?.[0]?.increment ?? 1);
    return [0, 1, 2].map((i) => +(snap.min_next_bid + i * inc).toFixed(2));
  }, [snap]);
  const amount = options[choice];
  const leading = st?.leading_team_id === user.team_id;
  let blockReason = "";
  if (snap && !snap.owner_bidding) blockReason = "Bids are placed by the auctioneer on stage";
  else if (!st || !BIDDABLE.includes(st.status)) blockReason = "Bidding is not open";
  else if (leading) blockReason = "You hold the highest bid";
  else if (me?.blocked?.length) blockReason = me.blocked[0];
  else if (me && amount > me.max_bid) blockReason = `Exceeds your max allowed bid (${fmtL(me.max_bid)})`;

  const placeBid = async () => {
    setBusy(true);
    try {
      await api.post(`/auction/${mine.team.club_id}/bid`, { amount, expected_version: st.version, client_bid_id: crypto.randomUUID() });
      toast.success(`Bid placed: ${fmtL(amount)}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!snap) return <div className="min-h-screen grid place-items-center text-slate-400">Connecting…</div>;
  const team = mine?.team || me;
  return (
    <div className="min-h-screen pb-10" data-testid="owner-portal">
      <SoldOverlay lastEvent={lastEvent} snap={snap} />
      <header className="sticky top-0 z-30 bg-[#050B14]/90 backdrop-blur border-b border-white/5 px-4 h-14 flex items-center justify-between">
        <Logo />
        <div className="flex items-center gap-3">
          <span data-testid="ws-status" className={connected ? "text-emerald-400" : "text-red-400"}>{connected ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}</span>
          <button onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn" className="p-2 text-slate-400"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 pt-6 space-y-6">
        <div className="flex items-center gap-4">
          <TeamBadge team={team} size={56} />
          <div>
            <p className="eyebrow">Team portal</p>
            <h1 className="text-2xl sm:text-3xl font-black" data-testid="owner-team-name">{team?.name}</h1>
            <p className="text-sm text-slate-400">Owner: {team?.owner_name}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Available purse" value={fmtL(team?.remaining_purse)} accent testid="owner-remaining-purse" />
          <Stat label="Spent" value={fmtL(team?.spent)} testid="owner-spent" />
          <Stat label="Squad" value={`${team?.players_count ?? 0}/${snap.config?.max_squad ?? "∞"}`} sub={`${Math.max(0, (snap.config?.max_squad || 0) - (team?.players_count || 0))} slots left`} testid="owner-squad-size" />
          <Stat label="Max bid now" value={fmtL(me?.max_bid)} sub="After squad reserve" testid="owner-max-bid" />
        </div>

        <div className="grid lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 surface-2 rounded-2xl p-5" data-testid="owner-live-panel">
            <div className="flex justify-between items-center"><p className="eyebrow">On the block</p><StatusPill status={st.status} /></div>
            {snap.player ? (
              <div className="flex gap-4 mt-4 items-center">
                <PlayerAvatar player={snap.player} size={84} />
                <div className="min-w-0">
                  <h2 className="text-2xl font-black truncate" data-testid="owner-player-name">{snap.player.name}</h2>
                  <p className="text-sm text-slate-400">{snap.player.role} · {snap.player.category_name} · Base {fmtL(snap.player.effective_base_price)}</p>
                </div>
              </div>
            ) : <p className="text-slate-500 mt-4">Waiting for the auctioneer to nominate a player</p>}
            <div className="grid grid-cols-2 gap-4 mt-6 items-end">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-widest">Current bid</p>
                <p className="num text-4xl font-black gold-text" data-testid="owner-current-bid">{st.current_bid != null ? fmtL(st.current_bid) : "—"}</p>
                <p className={`text-sm mt-1 ${leading ? "text-emerald-400 font-bold" : "text-slate-400"}`} data-testid="owner-leading-team">
                  {leading ? "You are leading" : snap.teams.find((t) => t.id === st.leading_team_id)?.name || "No bids yet"}
                </p>
              </div>
              <TimerBar snap={snap} offset={offset} />
            </div>
          </div>
          <div className="lg:col-span-2 surface rounded-2xl p-5 flex flex-col">
            <p className="eyebrow">Your paddle</p>
            <div className="grid grid-cols-3 gap-2 mt-4">
              {options.map((o, i) => (
                <button key={o} onClick={() => setChoice(i)} data-testid={`quick-bid-${i}`}
                  className={`py-3 rounded-lg border num font-bold text-sm transition-colors ${choice === i ? "border-[#E5B842] bg-[#E5B842]/15 text-[#F5D061]" : "border-white/10 hover:border-white/30"}`}>
                  {fmtL(o)}
                </button>
              ))}
            </div>
            <button onClick={placeBid} disabled={!!blockReason || busy || !amount} data-testid="place-bid-paddle-btn"
              className="mt-4 h-24 rounded-2xl gold-bg font-display font-black text-2xl flex items-center justify-center gap-3 active:scale-[0.98] transition-transform disabled:opacity-35 disabled:grayscale">
              {busy ? <Loader2 className="animate-spin" /> : <Gavel />} BID {amount ? fmtL(amount) : ""}
            </button>
            <p className="text-sm mt-3 min-h-[20px] text-orange-300" data-testid="bid-block-reason">{blockReason}</p>
          </div>
        </div>

        <Tabs defaultValue="squad">
          <TabsList className="bg-[#0B1323]">
            <TabsTrigger value="squad" data-testid="tab-squad">My squad</TabsTrigger>
            <TabsTrigger value="bids" data-testid="tab-bids">My bids</TabsTrigger>
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming</TabsTrigger>
          </TabsList>
          <TabsContent value="squad" className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {Object.entries(mine?.roles || {}).map(([r, n]) => <span key={r} className="surface rounded-full px-3 py-1 text-xs">{r}: <b className="num">{n}</b></span>)}
            </div>
            {mine?.warnings?.map((w) => <p key={w} className="text-xs text-orange-300" data-testid="squad-warning">⚠ {w}</p>)}
            <div className="grid sm:grid-cols-2 gap-2" data-testid="owner-squad-list">
              {mine?.players?.map((p) => (
                <div key={p.id} className="surface rounded-xl p-3 flex items-center gap-3">
                  <PlayerAvatar player={p} size={40} />
                  <div className="flex-1"><div className="font-semibold">{p.name}</div><div className="text-xs text-slate-400">{p.role}</div></div>
                  <span className="num font-bold text-[#F5D061]">{fmtL(p.sold_price)}</span>
                </div>
              ))}
              {!mine?.players?.length && <p className="text-slate-500 text-sm">No players purchased yet</p>}
            </div>
          </TabsContent>
          <TabsContent value="bids">
            <div className="surface rounded-xl divide-y divide-white/5" data-testid="owner-bid-history">
              {mine?.bids?.map((b) => (
                <div key={b.id} className="flex justify-between px-4 py-2 text-sm">
                  <span>{b.player_name}</span>
                  <span className={b.status === "accepted" ? "text-emerald-300" : b.status === "voided" ? "text-slate-500 line-through" : "text-red-300"} title={b.reason}>
                    <span className="num">{fmtL(b.amount)}</span> · {b.status}
                  </span>
                  <span className="text-slate-500 hidden sm:block">{fmtTime(b.created_at)}</span>
                </div>
              ))}
              {!mine?.bids?.length && <p className="p-4 text-slate-500 text-sm">No bids yet</p>}
            </div>
          </TabsContent>
          <TabsContent value="upcoming">
            <div className="grid sm:grid-cols-2 gap-2">
              {mine?.upcoming?.map((p) => (
                <div key={p.id} className="surface rounded-xl p-3 flex items-center gap-3"><PlayerAvatar player={p} size={36} /><div><div className="font-semibold text-sm">{p.name}</div><div className="text-xs text-slate-400">{p.role}</div></div></div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
